import { BatteryHealthStatus } from '../models'
import { fetchBatteryHealtStatusByIndex } from '../api/batteries.api'
import { fromHealthStatusDtoToModelTransformer, mergeBatteryHealthStatusItems } from '../transformers/from-health-status-dto-to-model-transformer'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { isUndefined } from 'lodash'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { usePortalQuery } from 'src/Libs/api'

interface UseBatteryHealthStatus {
  batteryHealthStatus: BatteryHealthStatus | null
  moduleCount: number
  isLoading: boolean
  isError: boolean
}

/**
 * Hook to fetch the battery health status
 */
export const useBatteryHealthStatus = (storageId: string | undefined, batteryIndex: string | undefined): UseBatteryHealthStatus => {
  const authFetch = useAuthFetchWithResponse()
  const { t } = useCustomTranslation()

  const queryFn = async (): Promise<{ result: BatteryHealthStatus | null; moduleCount: number }> => {
    const { error, hasErrors, value } = await fetchBatteryHealtStatusByIndex(storageId, batteryIndex, authFetch)

    if (hasErrors) {
      throw error || new Error('An unexpected error occurred while fetching battery health data')
    }

    const transfromedData = value?.map(item => fromHealthStatusDtoToModelTransformer(item, t))

    if (!transfromedData) return { result: null, moduleCount: 0 }

    return { result: mergeBatteryHealthStatusItems(transfromedData) as BatteryHealthStatus, moduleCount: transfromedData.length }
  }

  const { data, isLoading, isError } = usePortalQuery({
    queryKey: ['battery-health-status', storageId, batteryIndex],
    queryFn,
    enabled: !!storageId && !isUndefined(batteryIndex),
  })

  return {
    batteryHealthStatus: data?.result ?? null,
    moduleCount: data?.moduleCount ?? 0,
    isLoading,
    isError,
  }
}
